/**
 * Ray — pooled line-of-sight casts.
 *
 * `physics.raycast` allocates a fresh result object per call *and* walks the
 * dynamic body list on top of the BVH. Sixteen agents doing perception, hearing
 * and cover scoring is several hundred casts a second, all of it garbage.
 *
 * `physics.queries.raycast(origin, dir, far, mask, out)` writes into a caller-owned
 * result and only tests static world geometry, which is exactly what a
 * line-of-sight test means: a corpse or a rolling grenade must not block sight.
 *
 * Every call site owns one persistent `out` from `newRayResult()` and must treat
 * the returned object as valid only until its next cast.
 *
 * owner: ai
 */
import * as THREE from 'three';

/** A result object shaped like `physics.raycast`'s, reusable forever. */
export function newRayResult() {
  return {
    point: new THREE.Vector3(),
    normal: new THREE.Vector3(),
    distance: 0,
    triangle: -1,
    object: null,
    surface: 'concrete',
    body: null,
  };
}

/**
 * Cast against static world geometry, writing into `out` when the adapter
 * supports it. Falls back to the allocating public API so this degrades to
 * today's behaviour rather than to no line of sight at all.
 *
 * @returns {object|null} `out` on a hit, else null.
 */
export function castRay(physics, origin, dir, far, mask, out) {
  if (!physics) return null;
  const q = physics.queries;
  if (q && typeof q.raycast === 'function') {
    try { return q.raycast(origin, dir, far, mask, out); } catch { return null; }
  }
  try { return physics.raycast(origin, dir, far, mask); } catch { return null; }
}

/** True when anything solid sits between the two points. Allocation free. */
export function blocked(physics, from, to, mask, out, shorten = 0.12) {
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (d < 1e-4) return false;
  _d.set(dx / d, dy / d, dz / d);
  return !!castRay(physics, from, _d, d - shorten, mask, out);
}

const _d = new THREE.Vector3();
